import { useState, useEffect } from 'react';
import { getProjectUsers, assignUserToProject, endProjectAssignment, getUsers, PROJECT_FUNCTIONS, SYSTEM_ROLES } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Combobox } from './ui/combobox';
import { Plus, X, Users, Clock, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

export default function ProjectAssignments({ projectId, canEdit = false }) {
  const [assignments, setAssignments] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const { toast } = useToast();

  const [newAssignment, setNewAssignment] = useState({
    user_id: '',
    system_role: 'sales',
    project_function: 'venditore',
  });

  useEffect(() => {
    if (projectId) {
      loadData();
    }
  }, [projectId]);

  const loadData = async () => {
    try {
      const [assignmentsRes, pmUsers, supervisorUsers, salesUsers] = await Promise.all([
        getProjectUsers(projectId, showHistory),
        getUsers('project_manager'),
        getUsers('supervisor'),
        getUsers('venditore'),
      ]);
      setAssignments(assignmentsRes.data);
      // Merge PM, Supervisor and Sales users
      const pmData = pmUsers?.data || [];
      const supData = supervisorUsers?.data || [];
      const salesData = salesUsers?.data || [];
      const mergedUsers = [
        ...pmData.map(u => ({ ...u, availableRole: 'pm' })),
        ...supData.map(u => ({ ...u, availableRole: 'supervisor' })),
        ...salesData.map(u => ({ ...u, availableRole: 'sales' })),
      ];
      setAllUsers(mergedUsers);
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile caricare le assegnazioni', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddAssignment = async () => {
    if (!newAssignment.user_id) {
      toast({ title: 'Errore', description: 'Seleziona un utente', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      await assignUserToProject({
        project_id: projectId,
        user_id: newAssignment.user_id,
        system_role: newAssignment.system_role,
        project_function: newAssignment.project_function,
      });
      toast({ title: 'Utente assegnato', description: 'Assegnazione creata con successo' });
      setDialogOpen(false);
      setNewAssignment({ user_id: '', system_role: 'sales', project_function: 'venditore' });
      loadData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ') : 'Impossibile assegnare utente';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleEndAssignment = async (assignmentId) => {
    if (!window.confirm('Terminare questa assegnazione?')) return;
    
    try {
      await endProjectAssignment(assignmentId);
      toast({ title: 'Assegnazione terminata' });
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile terminare assegnazione', variant: 'destructive' });
    }
  };

  const getUserName = (userId) => {
    const user = allUsers.find(u => u.id === userId);
    return user ? user.name : 'Sconosciuto';
  };

  const getFunctionLabel = (functionValue) => {
    return PROJECT_FUNCTIONS.find(f => f.value === functionValue)?.label || functionValue;
  };

  const getRoleLabel = (roleValue) => {
    return SYSTEM_ROLES.find(r => r.value === roleValue)?.label || roleValue;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Attivo';
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy', { locale: it });
    } catch {
      return dateStr;
    }
  };

  // Filter active and historical assignments
  const activeAssignments = assignments.filter(a => !a.end_date);
  const historicalAssignments = assignments.filter(a => a.end_date);

  // Get available users (not already actively assigned)
  const activeUserIds = activeAssignments.map(a => a.user_id);
  const availableUsers = allUsers.filter(u => !activeUserIds.includes(u.id));

  // Filter users by selected system role
  const filteredAvailableUsers = availableUsers.filter(u => {
    if (newAssignment.system_role === 'pm') return u.availableRole === 'pm';
    if (newAssignment.system_role === 'supervisor') return u.availableRole === 'supervisor';
    if (newAssignment.system_role === 'sales') return u.availableRole === 'sales';
    return false;
  });

  // Generate helpful message when no users available
  const getEmptyMessage = () => {
    const role = SYSTEM_ROLES.find(r => r.value === newAssignment.system_role);
    const roleLabel = role ? role.label : newAssignment.system_role;
    if (allUsers.length === 0) {
      return 'Caricamento utenti...';
    }
    if (availableUsers.length === 0) {
      return `Tutti gli utenti sono già assegnati a questo progetto`;
    }
    return `Nessun ${roleLabel} disponibile`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-orange-600" />
          <h3 className="font-semibold">Team Progetto</h3>
          <Badge variant="outline">{activeAssignments.length} attivi</Badge>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setShowHistory(!showHistory);
              loadData();
            }}
          >
            <Clock className="w-4 h-4 mr-1" />
            {showHistory ? 'Nascondi Storico' : 'Mostra Storico'}
          </Button>
          {canEdit && (
            <Button type="button" size="sm" onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Aggiungi
            </Button>
          )}
        </div>
      </div>

      {/* Active Assignments */}
      {activeAssignments.length === 0 ? (
        <p className="text-sm text-gray-500 py-2">Nessun utente assegnato</p>
      ) : (
        <div className="space-y-2">
          {activeAssignments.map((assignment) => (
            <div
              key={assignment.id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-orange-700">
                    {getUserName(assignment.user_id).charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-sm">{getUserName(assignment.user_id)}</p>
                  <div className="flex gap-1">
                    <Badge variant="secondary" className="text-xs">
                      {getRoleLabel(assignment.system_role)}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {getFunctionLabel(assignment.project_function)}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">
                  Dal {formatDate(assignment.start_date)}
                </span>
                {canEdit && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-red-500 hover:text-red-700"
                    onClick={() => handleEndAssignment(assignment.id)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Historical Assignments */}
      {showHistory && historicalAssignments.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <h4 className="text-sm font-medium text-gray-500 mb-2">Storico Assegnazioni</h4>
          <div className="space-y-2">
            {historicalAssignments.map((assignment) => (
              <div
                key={assignment.id}
                className="flex items-center justify-between p-2 bg-gray-100 rounded-lg opacity-60"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm">{getUserName(assignment.user_id)}</span>
                  <Badge variant="outline" className="text-xs">
                    {getFunctionLabel(assignment.project_function)}
                  </Badge>
                </div>
                <span className="text-xs text-gray-500">
                  {formatDate(assignment.start_date)} - {formatDate(assignment.end_date)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Assignment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assegna Utente al Progetto</DialogTitle>
            <DialogDescription>
              Seleziona un utente e la sua funzione nel progetto
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Ruolo Sistema</Label>
              <Select
                value={newAssignment.system_role}
                onValueChange={(v) => setNewAssignment({ ...newAssignment, system_role: v, user_id: '' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SYSTEM_ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
            </Select>
            </div>

            <div className="space-y-2">
              <Label>Utente</Label>
              <Combobox
                options={filteredAvailableUsers.map((user) => ({
                  value: user.id,
                  label: `${user.name} (${user.email})`,
                }))}
                value={newAssignment.user_id}
                onChange={(v) => setNewAssignment({ ...newAssignment, user_id: v })}
                placeholder="Seleziona utente"
                searchPlaceholder="Cerca per nome o email..."
                emptyText={getEmptyMessage()}
              />
            </div>

            <div className="space-y-2">
              <Label>Funzione nel Progetto</Label>
              <Select
                value={newAssignment.project_function}
                onValueChange={(v) => setNewAssignment({ ...newAssignment, project_function: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_FUNCTIONS.map((func) => (
                    <SelectItem key={func.value} value={func.value}>
                      {func.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button type="button" onClick={handleAddAssignment} disabled={saving || !newAssignment.user_id}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Assegna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
